import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import Cookies from "js-cookie";
import { Divider, Tooltip, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ScrollableBox from "../../../../components/common/ScrollableBox";
import { useCommonContext } from "../../../../context/juno/CommonContext";
import { useJunoMethods } from "../../../../hooks/useJunoMethods";
import { setCreditTrigger } from "../../../../redux/slices/triggerSlice";
import { ImageInfo } from "../../../../types/junoTypes";
import DetailView from "./DetailView";
import ImagesOnlyView from "./ImagesOnlyView";
import ImageModal from "./imageModal/ImageModal";
import InputPrompt from "./InputPrompt";
import { alpha } from "@mui/material/styles";
import CollectionsIcon from "@mui/icons-material/Collections";
import { useCustomNavigate } from "../../../../hooks/useCustomNavigate";
import NotificationDisplay from "../../../../components/common/NotificationDisplay";

export default function History() {
  const { t } = useTranslation();
  const { fetchHistory } = useJunoMethods();
  const { refreshTrigger } = useCommonContext();
  const dispatch = useDispatch();
  const navigate = useCustomNavigate();

  const [requests, setRequests] = useState<[ImageInfo][]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [selectedImageUuid, setSelectedImageUuid] = useState<string | null>(null);
  const [openModal, setOpenModal] = React.useState(false);

  // 定数定義
  const POLLING_INITIAL_DELAY = 15000; // 15秒
  const POLLING_INTERVAL = 8000; // 8秒
  const SCROLL_DELAY = 100; // 100ミリ秒

  const [isPolling, setIsPolling] = useState(false);

  // Processing状態のリクエストが存在するかチェック
  const hasProcessingRequests = useMemo(() => {
    return requests.some((request) => request[0].request_status === "Processing");
  }, [requests]);

  // 画像一覧取得
  const fetchAndSetImageList = async (scroll = true) => {
    try {
      const json = await fetchHistory();
      let flat_json = json.flat();
      flat_json = flat_json.filter((image) => image.request_status === "Success");
      setModalImages(flat_json);
      setRequests(json);
      if (scroll) {
        scrollToBottom();
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // modalImagesの何番目にあるかさがす
    if (selectedImageUuid) {
      const index = modalImages.findIndex((image) => image.uuid === selectedImageUuid);
      setModalActiveIndex(index);
    }
  }, [openModal]);

  // マウント時、画像一覧取得
  useEffect(() => {
    fetchAndSetImageList();
  }, [refreshTrigger]);

  useEffect(() => {
    let intervalId: number | null = null;
    const startTimers = () => {
      intervalId = window.setInterval(() => {
        if (hasProcessingRequests) {
          setIsPolling(true);
          fetchAndSetImageList(false);
          dispatch(setCreditTrigger(true));
        } else {
          setIsPolling(false);
        }
      }, POLLING_INTERVAL);
    };

    const timeoutId = setTimeout(startTimers, POLLING_INITIAL_DELAY);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [requests]);

  // ポーリングが停止したらリクエストが更新されたと判断されるためスクロール
  useEffect(() => {
    if (!isPolling) {
      scrollToBottom();
    }
  }, [isPolling]);

  // リクエストヒストリー削除処理
  const handleConfirmedDelete = async (request_uuid: string) => {
    try {
      const url = "/api/v1/juno/generated-images";
      const csrftoken = Cookies.get("csrftoken");
      const headers = new Headers({
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      });
      fetch(url, {
        method: "DELETE",
        headers: headers,
        body: JSON.stringify({ uuid: request_uuid }),
      });

      // リストからも削除
      const newList = requests.filter((item) => {
        return item[0].request_uuid !== request_uuid;
      });
      setRequests(newList);
    } catch (e) {
      console.error(e);
    }
  };

  // 削除確認ダイアログを開く
  const handleDialogOpen = (uuid: string) => {
    setSelectedUuid(uuid);
    setDialogOpen(true);
  };

  // 読み込み時に一番下までスクロール.
  const scrollToBottom = () => {
    setTimeout(() => {
      const container = document.getElementById("history-container");
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth", // スクロールを滑らかにする
        });
      }
    }, SCROLL_DELAY);
  };

  const [activeIndexes, setActiveIndexes] = useState<{ [request_uuid: string]: number }>({});
  const updateActiveIndex = (request_uuid: string, newIndex: number) => {
    setActiveIndexes((prev) => ({ ...prev, [request_uuid]: newIndex }));
  };

  const [modalImages, setModalImages] = React.useState<ImageInfo[]>([]);
  const [modalActiveIndex, setModalActiveIndex] = React.useState(0);
  const [viewMode, setViewMode] = React.useState<"detail" | "imagesOnly">(() => {
    return (Cookies.get("viewMode") as "detail" | "imagesOnly") || "detail";
  });

  // viewModeが変更されたときにCookieに保存
  const handleViewModeChange = (mode: "detail" | "imagesOnly") => {
    setViewMode(mode);
    Cookies.set("viewMode", mode, { expires: 365 }); // 1年間保存
  };

  return (
    <>
      <Paper
        sx={{
          height: "100%",
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
          backdropFilter: "blur(10px)",
        }}
        elevation={0}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Grid container sx={{ my: 0.5, px: 2, alignItems: "center" }}>
            <Grid
              size={{
                xs: 12,
                sm: 2,
              }}
              sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-start" } }}
            >
              <Button
                variant="text"
                size="small"
                startIcon={<CollectionsIcon />}
                sx={{ color: "text.primary" }}
                onClick={() => {
                  navigate(`/juno/gallery`);
                }}
              >
                My Images
              </Button>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <NotificationDisplay notificationKey="juno-image" size="small" />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 2,
              }}
              sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-end" }, alignItems: "center" }}
            >
              <Typography variant={"subtitle2"} component={"h3"} sx={{ mr: 1 }}>
                {t("juno.history.title")}
              </Typography>

              <Tooltip title={viewMode === "detail" ? "画像モード" : "詳細モード"}>
                <IconButton
                  onClick={() => handleViewModeChange(viewMode === "detail" ? "imagesOnly" : "detail")}
                  size="small"
                >
                  {viewMode === "detail" ? <GridViewIcon fontSize="small" /> : <ViewListIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Divider />
          <ScrollableBox id={"history-container"} sx={{ overflowY: "auto", height: "100%" }}>
            {viewMode === "detail" ? (
              <DetailView
                requests={requests}
                activeIndexes={activeIndexes}
                updateActiveIndex={updateActiveIndex}
                setOpenModal={setOpenModal}
                setSelectedImageUuid={setSelectedImageUuid}
                handleDialogOpen={handleDialogOpen}
              />
            ) : (
              <ImagesOnlyView
                requests={requests}
                setOpenModal={setOpenModal}
                setSelectedImageUuid={setSelectedImageUuid}
                handleDialogOpen={handleDialogOpen}
              />
            )}
          </ScrollableBox>

          {/* 削除確認ダイアログ */}
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} disableEnforceFocus>
            <DialogTitle>{t("juno.history.delete.title")}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t("juno.history.delete.description")}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button disableElevation onClick={() => setDialogOpen(false)} color="primary">
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  handleConfirmedDelete(selectedUuid!);
                  setDialogOpen(false);
                }}
                color="error"
              >
                {t("common.delete")}
              </Button>
            </DialogActions>
          </Dialog>
          <Box flexGrow={1} />
          <InputPrompt fetchAndSetImageList={fetchAndSetImageList} />
        </Box>
      </Paper>
      {openModal && (
        <ImageModal
          images={modalImages}
          activeIndex={modalActiveIndex}
          setActiveIndex={setModalActiveIndex}
          open={openModal}
          setOpen={setOpenModal}
        />
      )}
    </>
  );
}
